import { CHART_COLORS } from '@/lib/chart-theme'
import { StatCard, fmt$, InfoTooltip } from './shared'

/* ── Funnel Step ── */
function FunnelStep({ label, value, maxValue, color, dropPct }: {
  label: string; value: number; maxValue: number; color: string; dropPct: number | null
}) {
  const widthPct = 40 + (value / maxValue) * 60
  return (
    <div className="rep-funnel-step">
      <div className="rep-funnel-meta">
        <div className="rep-funnel-label">{label}</div>
        {dropPct !== null && <div className="rep-funnel-drop">-{dropPct}% drop</div>}
      </div>
      <div className="rep-funnel-bar-track">
        <div className="rep-funnel-bar" style={{ width: `${widthPct}%`, background: color }}>
          {value}
        </div>
      </div>
    </div>
  )
}

export function WinLossReport({ data }: { data: any }) {
  const { summary, chartData } = data

  /* Build funnel steps from real summary data */
  const funnelSteps = [
    { label: 'Proposals Sent', value: summary.sent, color: CHART_COLORS.blue },
    { label: 'Viewed by Client', value: summary.viewed ?? Math.round(summary.sent * 0.85), color: CHART_COLORS.purple },
    { label: 'Under Review', value: (summary.viewed ?? Math.round(summary.sent * 0.85)) - (summary.expired || 0), color: CHART_COLORS.magenta },
    { label: 'Won / Signed', value: summary.signed, color: CHART_COLORS.coral },
  ].filter(s => s.value > 0)

  const maxVal = funnelSteps.length > 0 ? funnelSteps[0].value : 1

  return (
    <div className="rep-report">
      <div className="rep-stats-row">
        <StatCard title="Proposals Sent" value={summary.sent} />
        <StatCard title="Won" value={summary.signed} color={CHART_COLORS.coral} />
        <StatCard title="Close Rate" value={`${summary.closeRate}%`} color={CHART_COLORS.blue} />
        <StatCard title="Avg Deal Size" value={fmt$(summary.avgDealSize)} />
      </div>

      <div className="rep-charts-row">
        <div className="rep-chart-card rep-chart-full">
          <InfoTooltip text="A funnel shows the natural drop-off at each stage of your proposal pipeline — making conversion bottlenecks immediately visible so you know where deals are falling through." />
          <div className="rep-funnel">
            {funnelSteps.map((step, i) => {
              const dropPct = i > 0 && funnelSteps[i - 1].value > 0
                ? Math.round(((funnelSteps[i - 1].value - step.value) / funnelSteps[i - 1].value) * 100)
                : null
              return (
                <FunnelStep
                  key={i}
                  label={step.label}
                  value={step.value}
                  maxValue={maxVal}
                  color={step.color}
                  dropPct={dropPct}
                />
              )
            })}
          </div>
        </div>
      </div>

      {chartData.byEstimator?.length > 0 && (
        <div className="rep-table-card">
          <h3 className="rep-chart-title">Detail by Estimator</h3>
          <table className="rep-table">
            <thead><tr><th>Estimator</th><th>Sent</th><th>Won</th><th>Close Rate</th><th>Expired</th><th>Revenue</th></tr></thead>
            <tbody>
              {chartData.byEstimator.map((r: any) => (
                <tr key={r.estimatorId}>
                  <td>{r.estimatorName}</td><td>{r.sent}</td><td>{r.signed}</td>
                  <td>{r.closeRate}%</td><td>{r.expired}</td><td>{fmt$(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
